/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, message } from "antd";
import {
  AdminMssPage,
  AdminMssRecord,
  AdminMssText,
  fetchAdminMssRecords,
  fetchManagerSelfServiceData,
  getAvatarDetails,
  padEmpId,
} from "./managerSelfService.service";

const TEXT_OPTIONS: { value: AdminMssText; label: string }[] = [
  { value: "ALL", label: "All" },
  { value: "EMPLOYEES", label: "Employees" },
  { value: "DIRECT_REPORTING", label: "Direct Reporting" },
  { value: "ORG_UNITS_AND_POSITIONS", label: "Org Units & Positions" },
];

const na = (value: unknown) => {
  const text = String(value ?? "").trim();
  return text || "—";
};

function formatUpdated(value: string) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ApproverMssPanel: React.FC = () => {
  const [text, setText] = useState<AdminMssText>("ALL");
  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [reloadKey, setReloadKey] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdminMssPage | null>(null);
  const [selected, setSelected] = useState<AdminMssRecord | null>(null);
  const [detailTab, setDetailTab] = useState<"reports" | "org">("reports");

  const [activateEmpId, setActivateEmpId] = useState("");
  const [activating, setActivating] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [activatedTeam, setActivatedTeam] = useState<{ empId: string; names: string[] } | null>(null);

  const requestSeq = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const filtersKey = `${debouncedSearch}|${activeOnly}|${text}|${limit}`;
  const prevFilters = useRef(filtersKey);

  const load = useCallback(async (nextPage: number) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const pageResult = await fetchAdminMssRecords({
        text,
        status: activeOnly,
        search: debouncedSearch,
        page: nextPage,
        limit,
      });
      if (seq !== requestSeq.current) return;
      setResult(pageResult);
    } catch (err: any) {
      if (seq !== requestSeq.current) return;
      setResult(null);
      setError(err?.message || "Failed to load approver MSS records.");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [text, activeOnly, debouncedSearch, limit]);

  useEffect(() => {
    let nextPage = page;
    if (prevFilters.current !== filtersKey) {
      prevFilters.current = filtersKey;
      if (page !== 1) {
        setPage(1);
        return;
      }
      nextPage = 1;
    }
    load(nextPage);
  }, [filtersKey, page, reloadKey, load]);

  useEffect(() => {
    if (!selected || !result) return;
    const fresh = result.records.find((row) => row.id === selected.id);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [result, selected]);

  const activateEmployee = async (rawEmpId: string) => {
    const trimmed = rawEmpId.trim();
    if (!trimmed) {
      message.warning("Enter an employee ID.");
      return;
    }
    const formatted = padEmpId(trimmed);
    setActivating(true);
    const toastKey = "mss-activate";
    message.loading({ content: "Activating employee…", key: toastKey, duration: 0 });
    try {
      const activated = await fetchManagerSelfServiceData({
        display: "EMPLOYEES",
        empId: formatted,
        text: "Employees",
        forceSync: false,
      });
      const names = (activated.employees || []).map((emp) => emp.fullName).filter(Boolean);
      setActivatedTeam({ empId: formatted, names });
      message.success({
        content: activated.message || `Employee ${formatted} activated (${activated.totalCount}).`,
        key: toastKey,
        duration: 3,
      });
      setSearch(formatted);
      setDebouncedSearch(formatted);
      setPage(1);
      setSelected(null);
      setReloadKey((key) => key + 1);
    } catch (err: any) {
      message.error({
        content: err?.message || "Failed to activate employee.",
        key: toastKey,
        duration: 4,
      });
    } finally {
      setActivating(false);
    }
  };

  const refreshRecord = async (record: AdminMssRecord) => {
    setSyncingId(record.id);
    const toastKey = `mss-sync-${record.id}`;
    message.loading({ content: `Refreshing ${record.name}…`, key: toastKey, duration: 0 });
    try {
      const refreshed = await fetchManagerSelfServiceData({
        display: "EMPLOYEES",
        empId: record.empId,
        text: "Employees",
        forceSync: false,
      });
      message.success({
        content: refreshed.message || `${record.name} refreshed (${refreshed.totalCount}).`,
        key: toastKey,
        duration: 3,
      });
      setReloadKey((key) => key + 1);
    } catch (err: any) {
      message.error({
        content: err?.message || "Failed to refresh employee.",
        key: toastKey,
        duration: 4,
      });
    } finally {
      setSyncingId(null);
    }
  };

  const openRecord = (record: AdminMssRecord) => {
    setDetailTab(record.directReportsCount > 0 || record.orgUnitsCount === 0 ? "reports" : "org");
    setSelected(record);
  };

  const records = result?.records || [];
  const totalPages = Math.max(result?.totalPages || 1, 1);
  const rangeStart = records.length === 0 ? 0 : (page - 1) * (result?.limit || limit) + 1;
  const rangeEnd = records.length === 0 ? 0 : rangeStart + records.length - 1;

  return (
    <div className="mss-approver">
      <div className="mss-header-card">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <div>
            <div className="mss-header-title">
              <i className="ti ti-user-check"></i>
              Approver — Admin MSS Records
            </div>
            <p className="mss-header-subtitle mb-0">
              Activated managers, their direct reports, and org units.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
            onClick={() => setReloadKey((key) => key + 1)}
            disabled={loading}
          >
            <i className={`ti ti-refresh ${loading ? "fa-spin" : ""}`}></i>
            Refresh
          </button>
        </div>
        <div className="mss-stats-row">
          <div className="mss-stat-pill active-pill">
            <span>Records</span>
            <span className="mss-stat-count">{result?.total ?? 0}</span>
          </div>
          <div className="mss-stat-pill">
            <span>{activeOnly ? "Active" : "Inactive"}</span>
            <span className="mss-stat-count">{result?.filter || (activeOnly ? "ACTIVE" : "INACTIVE")}</span>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="fw-bold mb-1">Activate an employee</div>
          <div className="text-muted small mb-3">
            Loads that employee’s team into Manager Self-Service.
          </div>
          <form
            className="d-flex flex-column flex-md-row gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              activateEmployee(activateEmpId);
            }}
          >
            <input
              className="form-control"
              placeholder="Employee ID, e.g. 20990"
              value={activateEmpId}
              onChange={(event) => setActivateEmpId(event.target.value)}
              inputMode="numeric"
            />
            <button type="submit" className="btn btn-danger" disabled={activating}>
              {activating ? "Activating…" : "Activate"}
            </button>
          </form>
          {activatedTeam && (
            <div className="alert alert-success mt-3 mb-0">
              <div className="fw-semibold">Team loaded for {activatedTeam.empId}</div>
              <div className="small mt-1">
                {activatedTeam.names.length > 0
                  ? activatedTeam.names.join(", ")
                  : "No subordinates were returned."}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body d-flex flex-column flex-lg-row gap-2">
          <input
            className="form-control"
            placeholder="Search name or employee ID"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <select
            className="form-select"
            style={{ maxWidth: 240 }}
            value={text}
            onChange={(event) => setText(event.target.value as AdminMssText)}
          >
            {TEXT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className="form-select"
            style={{ maxWidth: 160 }}
            value={activeOnly ? "active" : "inactive"}
            onChange={(event) => setActiveOnly(event.target.value === "active")}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            className="form-select"
            style={{ maxWidth: 120 }}
            value={limit}
            onChange={(event) => setLimit(Number(event.target.value))}
          >
            {[20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} / page
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && !loading && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setReloadKey((key) => key + 1)}>
            Retry
          </button>
        </div>
      )}

      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Emp ID</th>
                <th>Status</th>
                <th>Direct</th>
                <th>Org Units</th>
                <th>Updated By</th>
                <th>Last Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && records.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-muted py-4 text-center">
                    Loading records…
                  </td>
                </tr>
              )}
              {!loading && records.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-muted py-4 text-center">
                    No records found.
                  </td>
                </tr>
              )}
              {records.map((record) => {
                const avatar = getAvatarDetails(record.name, record.empId);
                return (
                  <tr key={record.id}>
                    <td>
                      <button type="button" className="mss-person" onClick={() => openRecord(record)}>
                        <span
                          className="mss-avatar-sm"
                          style={{ background: avatar.bg, color: avatar.text }}
                        >
                          {avatar.initial}
                        </span>
                        <span className="mss-person-name">{record.name}</span>
                      </button>
                    </td>
                    <td className="font-monospace">{record.empId}</td>
                    <td>
                      <span className={`mss-status ${record.status ? "is-active" : "is-inactive"}`}>
                        {record.status ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td><span className="mss-count">{record.directReportsCount}</span></td>
                    <td><span className="mss-count">{record.orgUnitsCount}</span></td>
                    <td className="font-monospace">{na(record.updateBy)}</td>
                    <td className="text-nowrap">{formatUpdated(record.lastUpdated)}</td>
                    <td className="text-end text-nowrap">
                      <button type="button" className="mss-action" onClick={() => openRecord(record)}>
                        <i className="ti ti-eye"></i>
                        View
                      </button>
                      <button
                        type="button"
                        className="mss-action"
                        disabled={syncingId === record.id}
                        onClick={() => refreshRecord(record)}
                      >
                        <i className="ti ti-refresh"></i>
                        {syncingId === record.id ? "Refreshing" : "Refresh"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 px-3 py-3">
          <div className="text-muted small">
            Showing {rangeStart}–{rangeEnd} of {result?.total ?? 0}
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={page <= 1 || loading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </button>
            <span className="small">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <Modal
        open={!!selected}
        onCancel={() => setSelected(null)}
        footer={null}
        width={860}
        centered
        destroyOnHidden
        className="mss-view-modal"
        title="Team details"
      >
        {selected && (
          <RecordDetail
            record={selected}
            tab={detailTab}
            onTab={setDetailTab}
            refreshing={syncingId === selected.id}
            onRefresh={() => refreshRecord(selected)}
          />
        )}
      </Modal>
    </div>
  );
};

const RecordDetail: React.FC<{
  record: AdminMssRecord;
  tab: "reports" | "org";
  onTab: (tab: "reports" | "org") => void;
  refreshing: boolean;
  onRefresh: () => void;
}> = ({ record, tab, onTab, refreshing, onRefresh }) => {
  const avatar = getAvatarDetails(record.name, record.empId);
  const reports = record.directReports;
  const units = record.orgUnits;

  return (
    <div className="mss-detail">
      <div className="mss-detail-head">
        <span className="mss-avatar-lg" style={{ background: avatar.bg, color: avatar.text }}>
          {avatar.initial}
        </span>
        <div className="mss-detail-id">
          <div className="mss-detail-name">{record.name}</div>
          <div className="mss-detail-meta">
            <span className="font-monospace">{record.empId}</span>
            <span className={`mss-status ${record.status ? "is-active" : "is-inactive"}`}>
              {record.status ? "Active" : "Inactive"}
            </span>
          </div>
          <div className="mss-detail-sub">
            Updated by {na(record.updateBy)} · {formatUpdated(record.lastUpdated)}
          </div>
        </div>
        <button type="button" className="btn btn-outline-danger btn-sm" disabled={refreshing} onClick={onRefresh}>
          <i className="ti ti-refresh me-1"></i>
          {refreshing ? "Refreshing…" : "Refresh team"}
        </button>
      </div>

      <div className="mss-detail-stats">
        <div>
          <span>Direct reports</span>
          <strong>{record.directReportsCount}</strong>
        </div>
        <div>
          <span>Org units</span>
          <strong>{record.orgUnitsCount}</strong>
        </div>
        <div>
          <span>Total</span>
          <strong>{record.count}</strong>
        </div>
      </div>

      <div className="mss-detail-tabs">
        <button type="button" className={tab === "reports" ? "active" : ""} onClick={() => onTab("reports")}>
          Direct reports
          <span>{reports.length}</span>
        </button>
        <button type="button" className={tab === "org" ? "active" : ""} onClick={() => onTab("org")}>
          Org units & positions
          <span>{units.length}</span>
        </button>
      </div>

      {tab === "reports" ? (
        reports.length === 0 ? (
          <div className="mss-detail-empty">No direct reports for this employee.</div>
        ) : (
          <div className="mss-detail-list">
            {reports.map((person) => {
              const personAvatar = getAvatarDetails(person.name, person.empId);
              return (
                <div key={person.id} className="mss-detail-row">
                  <span className="mss-avatar-sm" style={{ background: personAvatar.bg, color: personAvatar.text }}>
                    {personAvatar.initial}
                  </span>
                  <div className="mss-detail-main">
                    <div className="fw-semibold">{person.name}</div>
                    <div className="text-muted font-monospace small">{na(person.empId)}</div>
                  </div>
                  <div className="mss-detail-field">
                    <span>Position</span>
                    <strong>{na(person.position)}</strong>
                  </div>
                  <div className="mss-detail-field">
                    <span>Org unit</span>
                    <strong>{na(person.orgUnit)}</strong>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : units.length === 0 ? (
        <div className="mss-detail-empty">No org units or positions for this employee.</div>
      ) : (
        <div className="mss-detail-list">
          {units.map((unit) => (
            <div key={unit.id} className="mss-detail-row">
              <span className="mss-org-icon">
                <i className="ti ti-building"></i>
              </span>
              <div className="mss-detail-main">
                <div className="fw-semibold">{na(unit.position)}</div>
                <div className="text-muted font-monospace small">{na(unit.objectId)}</div>
              </div>
              <div className="mss-detail-field">
                <span>Personnel area</span>
                <strong>{na(unit.personnelArea)}</strong>
              </div>
              <div className="mss-detail-field">
                <span>Subarea</span>
                <strong>{na(unit.subarea)}</strong>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApproverMssPanel;
